import React, { useState, useMemo } from 'react';
import {
  McpId, McpPolicyEntry, McpPolicyRule, McpPolicyWrite,
  putGlobalMcpPolicy, putUserMcpPolicy, deleteUserMcpPolicy,
} from '../api';
import { MCP_TOOL_CATALOG } from '../mcpToolCatalog';

export type EditorSubject =
  | { kind: 'global' }
  | { kind: 'user'; sub: string; email: string };

export interface McpAccessEditorProps {
  subject: EditorSubject;
  mcp: McpId;
  entry: McpPolicyEntry | null;
  protectedTools: Partial<Record<McpId, string[]>>;
  onClose: () => void;
  onSaved: (message: string) => void;
}

type Mode = 'inherit' | 'denied' | 'all' | 'all-except' | 'only' | 'custom';

function isGlobFree(pattern: string): boolean {
  return !pattern.includes('*');
}

function hasPolicyFields(e: McpPolicyEntry | null | undefined): boolean {
  return !!e && (e.allowed !== undefined || e.toolRules !== undefined || e.domainRules !== undefined);
}

function detectMode(entry: McpPolicyEntry | null, isUser: boolean, protectedSet: Set<string>): Mode {
  if (isUser && !hasPolicyFields(entry)) return 'inherit';
  if (!entry || (!hasPolicyFields(entry) && !isUser)) return 'all';
  if (entry.allowed === false) return 'denied';
  // Protected-tool denies are managed by the Protected-tools panel, not the freeform
  // mode. Exclude them so a plain "Allow all" (stored as N protected denies) is not
  // misread as "all-except".
  const rules = (entry.toolRules ?? []).filter(r => !(r.effect === 'deny' && protectedSet.has(r.pattern)));
  if (rules.length === 0) return 'all';
  const allDeny = rules.every(r => r.effect === 'deny');
  const allAllow = rules.every(r => r.effect === 'allow');
  const allGlobFree = rules.every(r => isGlobFree(r.pattern));
  if (allDeny && allGlobFree) return 'all-except';
  if (allAllow && allGlobFree) return 'only';
  return 'custom';
}

export const McpAccessEditor: React.FC<McpAccessEditorProps> = ({
  subject, mcp, entry, protectedTools, onClose, onSaved,
}) => {
  const isUser = subject.kind === 'user';
  const catalog = MCP_TOOL_CATALOG[mcp] ?? [];
  const protectedList = protectedTools[mcp] ?? [];
  const showProtectedPanel = isUser && protectedList.length > 0;
  const protectedSet = useMemo(() => new Set(protectedList), [protectedList]);

  // The freeform mode/picker operates on non-protected rules only; protected denies
  // are owned by the Protected-tools panel below.
  const freeformRules = useMemo(
    () => (entry?.toolRules ?? []).filter(r => !(r.effect === 'deny' && protectedSet.has(r.pattern))),
    [entry, protectedSet],
  );

  const initialMode = detectMode(entry, isUser, protectedSet);

  const initialSelected = useMemo((): Set<string> => {
    if (freeformRules.length === 0) return new Set();
    return new Set(freeformRules.map(r => r.pattern).filter(p => isGlobFree(p)));
  }, [freeformRules]);

  const [mode, setMode] = useState<Mode>(initialMode);
  const [selected, setSelected] = useState<Set<string>>(initialSelected);
  const [toolSearch, setToolSearch] = useState('');
  const [customRules, setCustomRules] = useState<McpPolicyRule[]>(freeformRules);
  const [domainRules, setDomainRules] = useState<McpPolicyRule[]>(entry?.domainRules ?? []);
  const [domainOpen, setDomainOpen] = useState(false);
  const [grants, setGrants] = useState<Set<string>>(() => new Set(entry?.protectedGrants ?? []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleGrant = (tool: string) => {
    setGrants(prev => {
      const next = new Set(prev);
      if (next.has(tool)) next.delete(tool);
      else next.add(tool);
      return next;
    });
  };

  const isCustomInitial = initialMode === 'custom';

  const filteredCatalog = useMemo(() =>
    catalog.filter(t => t.toLowerCase().includes(toolSearch.toLowerCase())),
    [catalog, toolSearch]
  );

  const toggleTool = (tool: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tool)) next.delete(tool);
      else next.add(tool);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      let write: McpPolicyWrite;

      if (mode === 'inherit') {
        if (isUser && hasPolicyFields(entry)) {
          await deleteUserMcpPolicy((subject as { kind: 'user'; sub: string }).sub, mcp);
          onSaved('Override removed — inheriting global default (bites within 60s).');
          return;
        }
        onSaved('No change — already inheriting global default.');
        return;
      } else if (mode === 'denied') {
        write = { allowed: false };
      } else if (mode === 'all') {
        write = { allowed: true, toolRules: [], domainRules };
      } else if (mode === 'all-except') {
        write = {
          allowed: true,
          toolRules: Array.from(selected).map(t => ({ pattern: t, effect: 'deny' as const })),
          domainRules,
        };
      } else if (mode === 'only') {
        write = {
          allowed: true,
          toolRules: Array.from(selected).map(t => ({ pattern: t, effect: 'allow' as const })),
          domainRules,
        };
      } else {
        // custom
        write = { allowed: true, toolRules: customRules, domainRules };
      }

      if (subject.kind === 'global') {
        await putGlobalMcpPolicy(mcp, write);
        onSaved(`Global ${mcp} policy saved (bites within 60s).`);
      } else {
        if (showProtectedPanel) {
          write = { ...write, protectedGrants: Array.from(grants) };
        }
        await putUserMcpPolicy((subject as { kind: 'user'; sub: string }).sub, mcp, write);
        onSaved(`${mcp} policy saved for ${(subject as { kind: 'user'; email: string }).email} (bites within 60s).`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const allModes: { value: Mode; label: string; userOnly?: boolean }[] = [
    { value: 'inherit', label: 'Inherit global default', userOnly: true },
    { value: 'denied', label: 'No access' },
    { value: 'all', label: 'Allow all tools' },
    { value: 'all-except', label: 'Allow all EXCEPT selected tools' },
    { value: 'only', label: 'Only allow selected tools' },
  ];
  if (isCustomInitial) {
    allModes.push({ value: 'custom', label: 'Custom (advanced pattern rules)' });
  }
  const availableModes = allModes.filter(m => !m.userOnly || isUser);

  const showToolPicker = mode === 'all-except' || mode === 'only';
  const showCustomRules = mode === 'custom';
  const showDomainInline = mcp === 'atlas-admin' && (mode === 'all' || mode === 'all-except' || mode === 'only');
  const showDomainToggle = mcp !== 'atlas-admin' && (mode === 'all' || mode === 'all-except' || mode === 'only');

  const ruleRows = (rules: McpPolicyRule[], setRules: (r: McpPolicyRule[]) => void, label: string) => (
    <div style={editorStyles.ruleSection}>
      <h4 style={editorStyles.ruleHeading}>{label}</h4>
      {rules.map((r, i) => (
        <div key={i} style={editorStyles.ruleRow}>
          <input
            style={editorStyles.ruleInput}
            value={r.pattern}
            placeholder="pattern (e.g. delete_*)"
            onChange={e => setRules(rules.map((x, j) => j === i ? { ...x, pattern: e.target.value } : x))}
          />
          <select
            style={editorStyles.ruleSelect}
            value={r.effect}
            onChange={e => setRules(rules.map((x, j) => j === i ? { ...x, effect: e.target.value as 'allow' | 'deny' } : x))}
          >
            <option value="allow">allow</option>
            <option value="deny">deny</option>
          </select>
          <button style={editorStyles.removeBtn} onClick={() => setRules(rules.filter((_, j) => j !== i))}>x</button>
        </div>
      ))}
      <button style={editorStyles.addRuleBtn} onClick={() => setRules([...rules, { pattern: '', effect: 'deny' }])}>
        + rule
      </button>
    </div>
  );

  const title = subject.kind === 'global'
    ? `Edit global access — ${mcp}`
    : `Edit access — ${mcp} for ${(subject as { kind: 'user'; email: string }).email}`;

  return (
    <div style={editorStyles.overlay}>
      <div style={editorStyles.modal}>
        <h2 style={editorStyles.title}>{title}</h2>

        {error && <div style={editorStyles.errorBanner}>{error}</div>}

        <div style={editorStyles.modeGroup}>
          {availableModes.map(m => (
            <label key={m.value} style={editorStyles.modeLabel}>
              <input
                type="radio"
                name="mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => setMode(m.value)}
                style={{ marginRight: '0.5rem' }}
              />
              {m.label}
            </label>
          ))}
        </div>

        {showToolPicker && (
          <>
            <div style={editorStyles.toolPicker}>
              <div style={editorStyles.toolPickerHeader}>
                <input
                  style={editorStyles.searchInput}
                  placeholder="Search tools..."
                  value={toolSearch}
                  onChange={e => setToolSearch(e.target.value)}
                />
                <span style={editorStyles.selectedCount}>{selected.size} selected</span>
              </div>
              <div style={editorStyles.toolList}>
                {filteredCatalog.map(tool => (
                  <label key={tool} style={editorStyles.toolItem}>
                    <input
                      type="checkbox"
                      checked={selected.has(tool)}
                      onChange={() => toggleTool(tool)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span style={editorStyles.toolName}>{tool}</span>
                  </label>
                ))}
              </div>
            </div>
            {mode === 'only' && selected.size === 0 && (
              <p style={{fontSize:'0.78rem', color:'#856404'}}>Select at least one tool — an empty "only" list cannot be saved.</p>
            )}
            {mode === 'all-except' && selected.size === 0 && (
              <p style={{fontSize:'0.78rem', color:'#888'}}>0 tools excluded — equivalent to Allow all.</p>
            )}
          </>
        )}

        {showCustomRules && (
          <div>
            <p style={editorStyles.customNote}>
              Advanced pattern rules (deny beats allow; a set containing allow rules denies anything unmatched)
            </p>
            {ruleRows(customRules, setCustomRules, 'Tool rules')}
          </div>
        )}

        {showDomainInline && ruleRows(domainRules, setDomainRules, 'Domain rules')}

        {showDomainToggle && (
          <div style={editorStyles.domainSection}>
            <button
              style={editorStyles.advancedToggle}
              onClick={() => setDomainOpen(o => !o)}
            >
              {domainOpen ? 'v' : '>'} Advanced: domain rules
            </button>
            {domainOpen && ruleRows(domainRules, setDomainRules, 'Domain rules')}
          </div>
        )}

        {showProtectedPanel && (
          <div style={editorStyles.protectedPanel}>
            <h4 style={editorStyles.protectedHeading}>Protected tools</h4>
            <p style={editorStyles.protectedHint}>
              Destructive tools, off by default. Toggle on to enable for this user.
            </p>
            <div style={editorStyles.protectedList}>
              {protectedList.map(tool => (
                <label key={tool} style={editorStyles.toolItem}>
                  <input
                    type="checkbox"
                    checked={grants.has(tool)}
                    onChange={() => toggleGrant(tool)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={editorStyles.toolName}>{tool}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={editorStyles.actions}>
          <button style={editorStyles.cancelBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            style={editorStyles.saveBtn}
            disabled={saving || (mode === 'only' && selected.size === 0) || (showCustomRules && customRules.some(r => !r.pattern)) || domainRules.some(r => !r.pattern)}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const editorStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    background: '#fff',
    borderRadius: 8,
    padding: '1.5rem',
    width: 'min(580px, 94vw)',
    maxHeight: '88vh',
    overflowY: 'auto',
  },
  title: { fontSize: '1.15rem', fontWeight: 700, marginBottom: '1rem' },
  errorBanner: {
    background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb',
    borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', fontSize: '0.85rem',
  },
  modeGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1rem',
    padding: '0.75rem',
    background: '#f9f9f9',
    borderRadius: 6,
    border: '1px solid #eee',
  },
  modeLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  toolPicker: {
    marginBottom: '1rem',
    border: '1px solid #ddd',
    borderRadius: 6,
    overflow: 'hidden',
  },
  toolPickerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0.75rem',
    background: '#f5f5f5',
    borderBottom: '1px solid #ddd',
  },
  searchInput: {
    flex: 1,
    padding: '0.35rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: '0.85rem',
  },
  selectedCount: { fontSize: '0.8rem', color: '#666', whiteSpace: 'nowrap' as const },
  toolList: {
    maxHeight: 240,
    overflowY: 'auto',
    padding: '0.25rem 0.5rem',
  },
  toolItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.2rem 0.25rem',
    cursor: 'pointer',
    borderRadius: 3,
    fontSize: '0.85rem',
  },
  toolName: { fontFamily: 'monospace', fontSize: '0.82rem' },
  customNote: {
    fontSize: '0.8rem', color: '#856404', background: '#fff3cd',
    borderRadius: 4, padding: '0.4rem 0.6rem', marginBottom: '0.5rem',
  },
  protectedPanel: {
    marginTop: '1rem', padding: '0.75rem 0.9rem',
    background: '#fff8f0', border: '1px solid #f0d9bf', borderRadius: 6,
  },
  protectedHeading: { fontSize: '0.9rem', fontWeight: 700, color: '#8a4b08', marginBottom: '0.25rem' },
  protectedHint: { fontSize: '0.78rem', color: '#8a6d3b', marginBottom: '0.5rem' },
  protectedList: { display: 'flex', flexDirection: 'column' as const, gap: '0.1rem' },
  domainSection: { marginTop: '0.75rem' },
  advancedToggle: {
    background: 'none',
    border: 'none',
    color: '#4a6cf7',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    padding: '0.25rem 0',
  },
  ruleSection: { marginTop: '0.75rem' },
  ruleHeading: { fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.4rem', color: '#444' },
  ruleRow: { display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' },
  ruleInput: {
    flex: 1, padding: '0.35rem 0.6rem', borderRadius: 4,
    border: '1px solid #ccc', fontSize: '0.85rem', fontFamily: 'monospace',
  },
  ruleSelect: { padding: '0.35rem', borderRadius: 4, border: '1px solid #ccc', fontSize: '0.85rem' },
  removeBtn: {
    background: 'transparent', border: '1px solid #dc3545', color: '#dc3545',
    padding: '0.25rem 0.45rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
  },
  addRuleBtn: {
    background: 'transparent', border: '1px solid #4a6cf7', color: '#4a6cf7',
    padding: '0.25rem 0.6rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.25rem' },
  cancelBtn: {
    background: 'transparent', border: '1px solid #aaa', color: '#555',
    padding: '0.4rem 1rem', borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
  },
  saveBtn: {
    background: '#4a6cf7', color: '#fff', border: 'none',
    padding: '0.4rem 1.1rem', borderRadius: 4, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
  },
};

export default McpAccessEditor;
